import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Box,
   Typography,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   Paper,
   Grid,
   Skeleton,
} from '@mui/material';
import { useGetQuotationPreviewQuery } from '../Redux/Service/QuotationApi';

function QuotationPreviewModal({ open, quotationId, onClose }) {
   const { data, isLoading, isError, error } = useGetQuotationPreviewQuery(quotationId, {
      skip: !open || !quotationId,
      refetchOnMountOrArgChange: true,
   });

   const quotation = data?.response;

   // Helper function to convert time type to hours per unit
   const getHoursPerUnit = (type) => {
      const typeStr = String(type).toLowerCase();
      if (typeStr === 'days') return 8;
      if (typeStr === 'months') return 160;
      return 40; // weeks is default
   };

   // Cost of one line item: the saved cost (fixed-cost and back-calculated rows have no
   // time x rate to recompute from), falling back to time x unit x rate for rows without one
   const quotationDetailsTotal = (item) => {
      if (!item) return 0;
      if (item.total !== null && item.total !== undefined && item.total !== '') {
         return Number(item.total) || 0;
      }
      const developmentTime = Number(item.developmentTime) || 0;
      const chargePerHour = Number(item.chargePerHour) || 0;
      const hoursPerUnit = getHoursPerUnit(item.type);
      return developmentTime * hoursPerUnit * chargePerHour;
   };

   // Calculate subtotal by summing all line item totals
   const calculateSubtotal = () => {
      if (!quotation?.quotationDetails) return 0;
      return quotation.quotationDetails.reduce((acc, item) => {
         return acc + quotationDetailsTotal(item);
      }, 0);
   };

   // Calculate GST amount
   const calculateGst = () => {
      const subtotal = calculateSubtotal();
      const gstPercentage = Number(quotation?.gstPercentage || 0);
      return (subtotal * gstPercentage) / 100;
   };

   // Calculate final total
   const calculateTotal = () => {
      return calculateSubtotal() + calculateGst();
   };

   return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
         <DialogTitle sx={{ fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>
            Quotation Preview
         </DialogTitle>
         <DialogContent sx={{ py: 3 }}>
            {isLoading ? (
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Skeleton variant="text" width="40%" height={30} />
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="50%" height={20} />
                  <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
               </Box>
            ) : isError ? (
               <Typography color="error">
                  Error loading quotation: {error?.data?.message || 'Unknown error'}
               </Typography>
            ) : quotation ? (
               <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Header Section */}
                  <Grid container spacing={2}>
                     <Grid item xs={12} sm={6}>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Quotation ID
                           </Typography>
                           <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {quotation.uniqueId}
                           </Typography>
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Date
                           </Typography>
                           <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {new Date(quotation.date).toDateString()}
                           </Typography>
                        </Box>
                     </Grid>
                  </Grid>

                  {/* Client Info */}
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Client Information
                     </Typography>
                     <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                           <Typography variant="caption" color="textSecondary">
                              Client Name
                           </Typography>
                           <Typography variant="body2">{quotation.clientName}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                           <Typography variant="caption" color="textSecondary">
                              Email
                           </Typography>
                           <Typography variant="body2">{quotation.clientEmail}</Typography>
                        </Grid>
                     </Grid>
                  </Box>

                  {/* Quotation Details */}
                  <Box>
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Details
                     </Typography>
                     <Box
                        sx={{
                           display: 'grid',
                           gridTemplateColumns: '1fr 1fr',
                           gap: 1,
                        }}
                     >
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Title
                           </Typography>
                           <Typography variant="body2">{quotation.title}</Typography>
                        </Box>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Place
                           </Typography>
                           <Typography variant="body2">{quotation.place}</Typography>
                        </Box>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Valid For (Days)
                           </Typography>
                           <Typography variant="body2">
                              {quotation.validTimeDay || 'N/A'}
                           </Typography>
                        </Box>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Estimates
                           </Typography>
                           <Typography variant="body2">
                              {quotation.estimate?.name || 'N/A'}
                           </Typography>
                        </Box>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Opportunity
                           </Typography>
                           <Typography variant="body2">
                              {quotation.opportunity?.name || 'N/A'}
                           </Typography>
                        </Box>
                        <Box>
                           <Typography variant="caption" color="textSecondary">
                              Service Provider
                           </Typography>
                           <Typography variant="body2">
                              {quotation.serviceProvider?.name || 'N/A'}
                           </Typography>
                        </Box>
                     </Box>
                  </Box>

                  {/* Description */}
                  {quotation.description && (
                     <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                           Description
                        </Typography>
                        <Typography
                           variant="body2"
                           sx={{ whiteSpace: 'pre-wrap' }}
                           dangerouslySetInnerHTML={{
                              __html: quotation.description || '',
                           }}
                        />
                     </Box>
                  )}

                  {/* Items Table */}
                  {quotation.quotationDetails && quotation.quotationDetails.length > 0 && (
                     <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                           Development Time & Cost Details
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                           <Table size="small">
                              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                 <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                                       Time
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                       Charge Per Hour
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                                       Total
                                    </TableCell>
                                 </TableRow>
                              </TableHead>
                              <TableBody>
                                 {quotation.quotationDetails.map((item, idx) => (
                                    <TableRow key={idx}>
                                       <TableCell>{item.description}</TableCell>
                                       <TableCell align="center">
                                          {item.developmentTime
                                             ? `${item.developmentTime} ${
                                                  item.type === 'One Time'
                                                     ? 'hours'
                                                     : item.type || 'Weeks'
                                               }`
                                             : '-'}
                                       </TableCell>
                                       <TableCell align="right">
                                          {quotation.currency?.symbol || '$'}{' '}
                                          {item.chargePerHour
                                             ? Number(item.chargePerHour).toLocaleString()
                                             : '-'}
                                       </TableCell>
                                       <TableCell align="right">
                                          {quotation.currency?.symbol || '$'}{' '}
                                          {Number(quotationDetailsTotal(item)).toLocaleString()}
                                       </TableCell>
                                    </TableRow>
                                 ))}
                              </TableBody>
                           </Table>
                        </TableContainer>
                     </Box>
                  )}

                  {/* Discounts Section */}
                  {quotation.quotationDiscounts && quotation.quotationDiscounts.length > 0 && (
                     <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                           Discounts
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                           <Table size="small">
                              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                 <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                                       Name
                                    </TableCell>
                                    <TableCell
                                       align="center"
                                       sx={{ fontWeight: 600, width: '25%' }}
                                    >
                                       Type
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: '25%' }}>
                                       Value
                                    </TableCell>
                                 </TableRow>
                              </TableHead>
                              <TableBody>
                                 {quotation.quotationDiscounts.map((discount, idx) => (
                                    <TableRow key={idx}>
                                       <TableCell sx={{ width: '50%' }}>
                                          {discount.discount?.name || 'N/A'}
                                       </TableCell>
                                       <TableCell align="center" sx={{ width: '25%' }}>
                                          {discount.type}
                                       </TableCell>
                                       <TableCell align="right" sx={{ width: '25%' }}>
                                          {discount.type === 'percentage'
                                             ? `${Number(discount.value).toLocaleString()}%`
                                             : `${quotation.currency?.symbol || '$'} ${Number(
                                                discount.value,
                                             ).toLocaleString()}`}
                                       </TableCell>
                                    </TableRow>
                                 ))}
                              </TableBody>
                           </Table>
                        </TableContainer>
                     </Box>
                  )}

                  {/* Services Section */}
                  {quotation.quotationAddons && quotation.quotationAddons.length > 0 && (
                     <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                           Services
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                           <Table size="small">
                              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                 <TableRow>
                                    <TableCell sx={{ fontWeight: 600, width: '50%' }}>
                                       Name
                                    </TableCell>
                                    <TableCell
                                       align="center"
                                       sx={{ fontWeight: 600, width: '25%' }}
                                    >
                                       Type
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, width: '25%' }}>
                                       Value
                                    </TableCell>
                                 </TableRow>
                              </TableHead>
                              <TableBody>
                                 {quotation.quotationAddons.map((addon, idx) => (
                                    <TableRow key={idx}>
                                       <TableCell sx={{ width: '50%' }}>
                                          {addon.addonService?.name || 'N/A'}
                                       </TableCell>
                                       <TableCell align="center" sx={{ width: '25%' }}>
                                          {addon.type}
                                       </TableCell>
                                       <TableCell align="right" sx={{ width: '25%' }}>
                                          {addon.type === 'percentage'
                                             ? `${Number(addon.value).toLocaleString()}%`
                                             : `${quotation.currency?.symbol || '$'} ${Number(
                                                addon.value,
                                             ).toLocaleString()}`}
                                       </TableCell>
                                    </TableRow>
                                 ))}
                              </TableBody>
                           </Table>
                        </TableContainer>
                     </Box>
                  )}

                  {/* Total Section */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                     <Box sx={{ minWidth: 250 }}>
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              mb: 1,
                              pb: 1,
                              borderBottom: '1px solid #e0e0e0',
                           }}
                        >
                           <Typography variant="body2">Subtotal:</Typography>
                           <Typography variant="body2">
                              {quotation.currency?.symbol || '$'}{' '}
                              {Number(calculateSubtotal()).toLocaleString()}
                           </Typography>
                        </Box>

                        {quotation.totalDiscountAmount > 0 && (
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 pt: 1,
                                 borderTop: '2px solid #333',
                              }}
                           >
                              <Typography variant="body2">TotalDiscount:</Typography>
                              <Typography variant="body2">
                                 -{" " + quotation.currency?.symbol || '$'}{' '}
                                 {(quotation.totalDiscountAmount ?? 0).toLocaleString()}
                              </Typography>
                           </Box>
                        )}
                        {quotation.totalAddOnAmount > 0 && (
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 pt: 1,
                                 borderTop: '2px solid #333',
                              }}
                           >
                              <Typography variant="body2">TotalAddOns:</Typography>
                              <Typography variant="body2">
                                 +{' ' + quotation.currency?.symbol || '$'}{' '}
                                 {(quotation.totalAddOnAmount ?? 0).toLocaleString()}
                              </Typography>
                           </Box>
                        )}
                        {quotation.gstPercentage > 0 && (
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 mb: 1,
                              }}
                           >
                              <Typography variant="body2">
                                 GST ({quotation.gstPercentage}%):
                              </Typography>
                              <Typography variant="body2">
                                 {quotation.currency?.symbol || '$'}{' '}
                                 {(quotation?.gst ?? 0).toLocaleString()}
                              </Typography>
                           </Box>
                        )}
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              pt: 1,
                              borderTop: '2px solid #333',
                           }}
                        >
                           <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Total:
                           </Typography>
                           <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {quotation.currency?.symbol || '$'}{' '}
                              {Number(quotation?.netTotal ?? 0).toLocaleString()}
                           </Typography>
                        </Box>
                     </Box>
                  </Box>

                  {/* Note */}
                  {quotation.note && (
                     <Box
                        sx={{
                           p: 2,
                           bgcolor: '#fff3cd',
                           borderRadius: 1,
                           borderLeft: '4px solid #ffc107',
                        }}
                     >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#856404' }}>
                           Note:
                        </Typography>
                        <Typography
                           variant="body2"
                           sx={{ color: '#856404', mt: 0.5 }}
                           dangerouslySetInnerHTML={{
                              __html: quotation.note || '',
                           }}
                        />
                     </Box>
                  )}
               </Box>
            ) : (
               <Typography>No quotation data available</Typography>
            )}
         </DialogContent>
         <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
            <Button onClick={onClose} variant="contained">
               Close
            </Button>
         </DialogActions>
      </Dialog >
   );
}

export default QuotationPreviewModal;
